import React, { useState } from "react";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OverviewStats } from "@/components/reports/OverviewStats";
import { FlashcardsAnalytics } from "@/components/reports/FlashcardsAnalytics";
import { QuizzesAnalytics } from "@/components/reports/QuizzesAnalytics";
import { ExamsAnalytics } from "@/components/reports/ExamsAnalytics";
import { ChatsAnalytics } from "@/components/reports/ChatsAnalytics";
import { StudyTimeCard } from "@/components/reports/StudyTimeCard";
import { PdfExportDropdown, PdfExportOptions } from "@/components/reports/PdfExportDropdown";
import { useReportsData } from "@/hooks/useReportsData";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpenCheck, BookCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const OverviewIcon = ({ className }: { className?: string }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.8 1L2.74967 0.99997C2.52122 0.999752 2.32429 0.999564 2.14983 1.04145C1.60136 1.17312 1.17312 1.60136 1.04145 2.14983C0.999564 2.32429 0.999752 2.52122 0.99997 2.74967L1 2.8V5.2L0.99997 5.25033C0.999752 5.47878 0.999564 5.67572 1.04145 5.85017C1.17312 6.39864 1.60136 6.82688 2.14983 6.95856C2.32429 7.00044 2.52122 7.00025 2.74967 7.00003L2.8 7H5.2L5.25033 7.00003C5.47878 7.00025 5.67572 7.00044 5.85017 6.95856C6.39864 6.82688 6.82688 6.39864 6.95856 5.85017C7.00044 5.67572 7.00025 5.47878 7.00003 5.25033L7 5.2V2.8L7.00003 2.74967C7.00025 2.52122 7.00044 2.32429 6.95856 2.14983C6.82688 1.60136 6.39864 1.17312 5.85017 1.04145C5.67572 0.999564 5.47878 0.999752 5.25033 0.99997L5.2 1H2.8ZM2.38328 2.01382C2.42632 2.00348 2.49222 2 2.8 2H5.2C5.50779 2 5.57369 2.00348 5.61672 2.01382C5.79955 2.05771 5.94229 2.20045 5.98619 2.38328C5.99652 2.42632 6 2.49222 6 2.8V5.2C6 5.50779 5.99652 5.57369 5.98619 5.61672C5.94229 5.79955 5.79955 5.94229 5.61672 5.98619C5.57369 5.99652 5.50779 6 5.2 6H2.8C2.49222 6 2.42632 5.99652 2.38328 5.98619C2.20045 5.94229 2.05771 5.79955 2.01382 5.61672C2.00348 5.57369 2 5.50779 2 5.2V2.8C2 2.49222 2.00348 2.42632 2.01382 2.38328C2.05771 2.20045 2.20045 2.05771 2.38328 2.01382ZM9.8 1L9.74967 0.99997C9.52122 0.999752 9.32429 0.999564 9.14983 1.04145C8.60136 1.17312 8.17312 1.60136 8.04145 2.14983C7.99956 2.32429 7.99975 2.52122 7.99997 2.74967L8 2.8V5.2L7.99997 5.25033C7.99975 5.47878 7.99956 5.67572 8.04145 5.85017C8.17312 6.39864 8.60136 6.82688 9.14983 6.95856C9.32429 7.00044 9.52122 7.00025 9.74967 7.00003L9.8 7H12.2L12.2503 7.00003C12.4788 7.00025 12.6757 7.00044 12.8502 6.95856C13.3986 6.82688 13.8269 6.39864 13.9586 5.85017C14.0004 5.67572 14.0003 5.47878 14 5.25033L14 5.2V2.8L14 2.74967C14.0003 2.52122 14.0004 2.32429 13.9586 2.14983C13.8269 1.60136 13.3986 1.17312 12.8502 1.04145C12.6757 0.999564 12.4788 0.999752 12.2503 0.99997L12.2 1H9.8ZM9.38328 2.01382C9.42632 2.00348 9.49222 2 9.8 2H12.2C12.5078 2 12.5737 2.00348 12.6167 2.01382C12.7995 2.05771 12.9423 2.20045 12.9862 2.38328C12.9965 2.42632 13 2.49222 13 2.8V5.2C13 5.50779 12.9965 5.57369 12.9862 5.61672C12.9423 5.79955 12.7995 5.94229 12.6167 5.98619C12.5737 5.99652 12.5078 6 12.2 6H9.8C9.49222 6 9.42632 5.99652 9.38328 5.98619C9.20045 5.94229 9.05771 5.79955 9.01382 5.61672C9.00348 5.57369 9 5.50779 9 5.2V2.8C9 2.49222 9.00348 2.42632 9.01382 2.38328C9.05771 2.20045 9.20045 2.05771 9.38328 2.01382ZM2.74967 7.99997L2.8 8H5.2L5.25033 7.99997C5.47878 7.99975 5.67572 7.99956 5.85017 8.04145C6.39864 8.17312 6.82688 8.60136 6.95856 9.14983C7.00044 9.32429 7.00025 9.52122 7.00003 9.74967L7 9.8V12.2L7.00003 12.2503C7.00025 12.4788 7.00044 12.6757 6.95856 12.8502C6.82688 13.3986 6.39864 13.8269 5.85017 13.9586C5.67572 14.0004 5.47878 14.0003 5.25033 14L5.2 14H2.8L2.74967 14C2.52122 14.0003 2.32429 14.0004 2.14983 13.9586C1.60136 13.8269 1.17312 13.3986 1.04145 12.8502C0.999564 12.6757 0.999752 12.4788 0.99997 12.2503L1 12.2V9.8L0.99997 9.74967C0.999752 9.52122 0.999564 9.32429 1.04145 9.14983C1.17312 8.60136 1.60136 8.17312 2.14983 8.04145C2.32429 7.99956 2.52122 7.99975 2.74967 7.99997ZM2.8 9C2.49222 9 2.42632 9.00348 2.38328 9.01382C2.20045 9.05771 2.05771 9.20045 2.01382 9.38328C2.00348 9.42632 2 9.49222 2 9.8V12.2C2 12.5078 2.00348 12.5737 2.01382 12.6167C2.05771 12.7995 2.20045 12.9423 2.38328 12.9862C2.42632 12.9965 2.49222 13 2.8 13H5.2C5.50779 13 5.57369 12.9965 5.61672 12.9862C5.79955 12.9423 5.94229 12.7995 5.98619 12.6167C5.99652 12.5737 6 12.5078 6 12.2V9.8C6 9.49222 5.99652 9.42632 5.98619 9.38328C5.94229 9.20045 5.79955 9.05771 5.61672 9.01382C5.57369 9.00348 5.50779 9 5.2 9H2.8ZM9.8 8L9.74967 7.99997C9.52122 7.99975 9.32429 7.99956 9.14983 8.04145C8.60136 8.17312 8.17312 8.60136 8.04145 9.14983C7.99956 9.32429 7.99975 9.52122 7.99997 9.74967L8 9.8V12.2L7.99997 12.2503C7.99975 12.4788 7.99956 12.6757 8.04145 12.8502C8.17312 13.3986 8.60136 13.8269 9.14983 13.9586C9.32429 14.0004 9.52122 14.0003 9.74967 14L9.8 14H12.2L12.2503 14C12.4788 14.0003 12.6757 14.0004 12.8502 13.9586C13.3986 13.8269 13.8269 13.3986 13.9586 12.8502C14.0004 12.6757 14.0003 12.4788 14 12.2503L14 12.2V9.8L14 9.74967C14.0003 9.52122 14.0004 9.32429 13.9586 9.14983C13.8269 8.60136 13.3986 8.17312 12.8502 8.04145C12.6757 7.99956 12.4788 7.99975 12.2503 7.99997L12.2 8H9.8ZM9.38328 9.01382C9.42632 9.00348 9.49222 9 9.8 9H12.2C12.5078 9 12.5737 9.00348 12.6167 9.01382C12.7995 9.05771 12.9423 9.20045 12.9862 9.38328C12.9965 9.42632 13 9.49222 13 9.8V12.2C13 12.5078 12.9965 12.5737 12.9862 12.6167C12.9423 12.7995 12.7995 12.9423 12.6167 12.9862C12.5737 12.9965 12.5078 13 12.2 13H9.8C9.49222 13 9.42632 12.9965 9.38328 12.9862C9.20045 12.9423 9.05771 12.7995 9.01382 12.6167C9.00348 12.5737 9 12.5078 9 12.2V9.8C9 9.49222 9.00348 9.42632 9.01382 9.38328C9.05771 9.20045 9.20045 9.05771 9.38328 9.01382Z"
      fill="currentColor"
    />
  </svg>
);

const FlashcardsIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 256 256"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M180,72H36A20,20,0,0,0,16,92V204a20,20,0,0,0,20,20H180a20,20,0,0,0,20-20V92A20,20,0,0,0,180,72Zm-4,128H40V96H176ZM240,52V176a12,12,0,0,1-24,0V56H64a12,12,0,0,1,0-24H220A20,20,0,0,1,240,52Z"
    />
  </svg>
);

const ChatIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 256 256"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M128,20A108,108,0,0,0,31.85,177.23L21,209.66A20,20,0,0,0,46.34,235l32.43-10.81A108,108,0,1,0,128,20Zm0,192a84,84,0,0,1-42.06-11.27,12,12,0,0,0-6-1.62,12.1,12.1,0,0,0-3.8.62l-29.79,9.93,9.93-29.79a12,12,0,0,0-1-9.81A84,84,0,1,1,128,212Z"
    />
  </svg>
);

export const ReportsPage: React.FC = () => {
  const { data, isLoading, error } = useReportsData();
  const [activeTab, setActiveTab] = useState("overview");
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (options: PdfExportOptions) => {
    if (!data || isExporting) return;

    setIsExporting(true);
    toast.loading("Preparing report data…", { id: "export-report" });

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.error("Please sign in to download reports", { id: "export-report" });
        return;
      }

      // Prepare report data
      const totalMastered = data.flashcardDecks.reduce(
        (sum, deck) => sum + deck.mastered_count,
        0
      );
      const totalCards = data.flashcardDecks.reduce(
        (sum, deck) => sum + deck.total_cards,
        0
      );

      const totalCorrect =
        data.quizAttempts.reduce((sum, a) => sum + (a.total_correct || 0), 0) +
        data.examAttempts.reduce((sum, a) => sum + (a.total_correct || 0), 0);
      const totalQuestions =
        data.quizAttempts.reduce((sum, a) => sum + a.total_questions, 0) +
        data.examAttempts.reduce((sum, a) => sum + a.total_questions, 0);
      const overallAccuracy =
        totalQuestions > 0
          ? Math.round((totalCorrect / totalQuestions) * 100)
          : 0;

      const reportPayload = {
        currentStreak: data.profile?.current_streak || 0,
        weeklyStudySessions: data.weeklyStudySessions,
        overallAccuracy,
        cardsMastered: totalMastered,
        totalCards,
        studyTime: {
          totalMinutes: data.studyTime.totalMinutes,
          flashcardsMinutes: data.studyTime.flashcardsMinutes,
          quizzesMinutes: data.studyTime.quizzesMinutes,
          examsMinutes: data.studyTime.examsMinutes,
        },
        flashcardDecks: data.flashcardDecks.map((d) => ({
          title: d.title,
          total_cards: d.total_cards,
          mastered_count: d.mastered_count,
        })),
        quizAttempts: data.quizAttempts.map((a) => ({
          score: a.score,
          total_questions: a.total_questions,
          difficulty: a.difficulty,
          completed_at: a.completed_at,
        })),
        examAttempts: data.examAttempts.map((a) => ({
          score: a.score,
          total_questions: a.total_questions,
          title: a.title,
          submitted_at: a.submitted_at,
        })),
        options, // Pass customization options to the edge function
        activeTab,
        generatedAt: new Date().toISOString(),
      };

      toast.loading("Generating PDF…", { id: "export-report" });

      const response = await supabase.functions.invoke("export-report-pdf", {
        body: reportPayload,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const { downloadUrl } = response.data;

      toast.loading("Downloading report…", { id: "export-report" });

      const downloadResponse = await fetch(downloadUrl);
      if (!downloadResponse.ok) {
        throw new Error("Failed to download report file");
      }

      const blob = await downloadResponse.blob();
      const url = URL.createObjectURL(blob);

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const filename = `shattara-learning-report-${year}-${month}-${day}.pdf`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully!", { id: "export-report" });
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to generate report. Please try again.", {
        id: "export-report",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DashboardShell 
      headerExtra={
        <PdfExportDropdown 
          onExport={handleExport} 
          isExporting={isExporting} 
          variant="desktop" 
        />
      }
      headerExtraMobile={
        <PdfExportDropdown 
          onExport={handleExport} 
          isExporting={isExporting} 
          variant="mobile" 
        />
      }
    >
      <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto w-full">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-foreground sm:text-3xl">
              Reports
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Track your learning progress and identify areas for improvement
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full max-w-3xl grid-cols-5 bg-muted/50">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <OverviewIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="chats" className="flex items-center gap-2">
                <ChatIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Chats</span>
              </TabsTrigger>
              <TabsTrigger value="flashcards" className="flex items-center gap-2">
                <FlashcardsIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Flashcards</span>
              </TabsTrigger>
              <TabsTrigger value="quizzes" className="flex items-center gap-2">
                <BookOpenCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Quizzes</span>
              </TabsTrigger>
              <TabsTrigger value="exams" className="flex items-center gap-2">
                <BookCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Exams</span>
              </TabsTrigger>
            </TabsList>

            {isLoading || !data ? (
              <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-32 rounded-xl" />
                  ))}
                </div>
                <Skeleton className="h-80 rounded-xl" />
              </div>
            ) : error ? (
              <div className="flex h-64 items-center justify-center rounded-xl border border-border bg-card">
                <p className="text-muted-foreground">Failed to load reports data</p>
              </div>
            ) : (
              <>
                <TabsContent value="overview" className="mt-0 space-y-6">
                  <OverviewStats data={data} />
                  <div className="grid gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2 space-y-6">
                      <div className="grid gap-6 lg:grid-cols-2">
                        <ChatsAnalytics data={data} compact />
                        <FlashcardsAnalytics data={data} compact />
                      </div>
                      <div className="grid gap-6 lg:grid-cols-2">
                        <QuizzesAnalytics data={data} compact />
                        <ExamsAnalytics data={data} compact />
                      </div>
                    </div>
                    <div>
                      <StudyTimeCard studyTime={data.studyTime} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="flashcards" className="mt-0 space-y-6">
                  <FlashcardsAnalytics data={data} />
                </TabsContent>

                <TabsContent value="quizzes" className="mt-0 space-y-6">
                  <QuizzesAnalytics data={data} />
                </TabsContent>

                <TabsContent value="exams" className="mt-0 space-y-6">
                  <ExamsAnalytics data={data} />
                </TabsContent>

                <TabsContent value="chats" className="mt-0 space-y-6">
                  <ChatsAnalytics data={data} />
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </main>
    </DashboardShell>
  );
};

export default ReportsPage;
